import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, Input} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { NavigateService } from 'src/app/core/services/navigate.service';
import { ProductDownloadService } from 'src/app/core/services/product-download.service';
import { ProductDto } from 'src/app/model/ProductDto';
import { BugGravities, BugInquiries, ProjectBugReleases } from 'src/app/model/ProjectBugReleases';
import { GravityElement, ProductBugDetailsComponent } from '../product-bug-details/product-bug-details.component';
import { MatDialog } from '@angular/material/dialog';
import { CmsService } from 'src/app/core/services/cms.service';
import { WorkFlowService } from 'src/app/core/services/work-flow.service';
import { CmsObject } from 'src/app/model/cmsobject';
import { ReportsService } from 'src/app/core/services/reports.service';

@Component({
  selector: 'ms-bugs-report',
  templateUrl: './bugs-report.component.html',
  styleUrls: ['./bugs-report.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})

export class BugsReportComponent implements OnInit {

  constructor(
    private productDownloadService: ProductDownloadService,
    private navigateService: NavigateService,
      public dialog: MatDialog,
      public cmsService: CmsService,
      public workFlow: WorkFlowService,
      public reportsService: ReportsService
  ) {}

  @Input() productDto: ProductDto = null;
  @Input() title: string = '';
  @Input() date: string = '';
  @Input() isLoading: boolean = false;
  @Input() functionalAreasTable: MatTableDataSource<string> = new MatTableDataSource<string>();
  @Input() functionalAreasBugsTables: Map<string, MatTableDataSource<ProjectBugReleases>> = new Map<string, MatTableDataSource<ProjectBugReleases>>();
  @Input() expandedElement: string | null;
  @Input() localizedFields: Map<string, string> = new Map<string, string>();

  public cmsObj: CmsObject[];
  public cmsObj2: CmsObject[];
  public cmsObj3: CmsObject[];
  public gravityTexts: BugGravities;

  displayedColumns: string[] = ['area','expand'];
  displayedBugColumns: string[] = ['nr','description'];
  bugsReportReleseNotesDisclaimer: string;
  bugsReportReleseNotesDisclaimerDetail: string;

  async ngOnInit(): Promise<void> {
    try{
      await this.getCmsObj();
    } catch(err){}
  }

  async getCmsObj() {
    this.cmsObj = await this.cmsService.getCmsObj(this.workFlow.getLanguageForCms(), 'bugsReportReleseNotes');
    this.bugsReportReleseNotesDisclaimer = this.cmsObj.find((x) => x.ContentName == 'bugsReportReleseNotes.Disclaimer').ContentTranslation;
    this.cmsObj2 = await this.cmsService.getCmsObj(this.workFlow.getLanguageForCms(), 'bugSeverityText');
    this.gravityTexts = new BugGravities (
      this.cmsObj2.find((x) => x.ContentName == 'bugSeverityText.bugSeverityGravity1').ContentTranslation,
      this.cmsObj2.find((x) => x.ContentName == 'bugSeverityText.bugSeverityGravity2').ContentTranslation,
      this.cmsObj2.find((x) => x.ContentName == 'bugSeverityText.bugSeverityGravity3').ContentTranslation,
      this.cmsObj2.find((x) => x.ContentName == 'bugSeverityText.bugSeverityGravity4').ContentTranslation,
      this.cmsObj2.find((x) => x.ContentName == 'bugSeverityText.bugSeverityGravity5').ContentTranslation,
      this.cmsObj2.find((x) => x.ContentName == 'bugSeverityText.bugSeverityGravityUndefined').ContentTranslation
    )

    this.cmsObj3 = await this.cmsService.getCmsObj(this.workFlow.getLanguageForCms(), 'bugsReportReleseNotes');
    this.bugsReportReleseNotesDisclaimerDetail = this.cmsObj3.find((x) => x.ContentName == 'bugsReportReleseNotes.DetailDisclaimer').ContentTranslation;
  }

  tableGravity(): GravityElement[]  {
    let gravityElements: GravityElement[] = [];
    const gravityItem1 = { gravityNumber: '1', gravityDescription: this.gravityTexts.Gravity1 };
    gravityElements.push(gravityItem1);
    const gravityItem2 = { gravityNumber: '2', gravityDescription: this.gravityTexts.Gravity2 };
    gravityElements.push(gravityItem2);
    const gravityItem3 = { gravityNumber: '3', gravityDescription: this.gravityTexts.Gravity3 };
    gravityElements.push(gravityItem3);
    const gravityItem4 = { gravityNumber: '4', gravityDescription: this.gravityTexts.Gravity4 };
    gravityElements.push(gravityItem4);
    const gravityItem5 = { gravityNumber: '5', gravityDescription: this.gravityTexts.Gravity5 };
    gravityElements.push(gravityItem5);
    return gravityElements;
  }

  async getPdf(){
    if(this.productDto.ProductCode)
      await this.productDownloadService.createBugPDF(`${this.productDto.ProductCode} rel${this.productDto.ReleaseNr ?? ' all'}`, this.date, this.getAllBugs(), this.functionalAreasTable.data);
    else
      await this.productDownloadService.createBugPDF(`Custom search`, new Date(Date.now()).toLocaleDateString(), this.getAllBugs(), this.functionalAreasTable.data);
  }

  getAllBugs(): ProjectBugReleases[]{
    let bugs: ProjectBugReleases[] = []
    this.functionalAreasBugsTables.forEach((item) => { bugs = bugs.concat(item.data)})
    return bugs;
  }

  public async openBug(bug: ProjectBugReleases) {
    try {
      bug.Inquiries = this.reportsService.setObjectsEmptyFields<BugInquiries>(bug.Inquiries)
      let archiviedDocuments = await this.productDownloadService.getProjectArchiviedDocument(bug.BugID, "bug")
      if(!archiviedDocuments.Result) throw new Error();
      this.navigateService.setLocalStorageData('archiviedDocuments', JSON.stringify(archiviedDocuments.Content))
    }
    catch (err){}
    finally {
      this.navigateService.setLocalStorageData('bugGravities', JSON.stringify(this.gravityTexts))
      this.navigateService.setLocalStorageData('bugGravityTables', JSON.stringify(this.tableGravity()))
      this.navigateService.setLocalStorageData('bugsReportReleseNotesDisclaimer', JSON.stringify(this.bugsReportReleseNotesDisclaimer))
      this.navigateService.setLocalStorageData('bugsReportReleseNotesDisclaimerDetail', JSON.stringify(this.bugsReportReleseNotesDisclaimerDetail))
      this.navigateService.setLocalStorageData('bug', JSON.stringify(bug))
      this.navigateService.setLocalStorageData('productDto', JSON.stringify(this.productDto))
      this.dialog.open(ProductBugDetailsComponent);
    }
  }

  public setParameters(row: string) {
    let a = this.functionalAreasBugsTables;
    this.expandedElement = this.expandedElement === row ? null : row;
  }
}
